// app/javascript/controllers/contact_controller.js
import { Controller } from "@hotwired/stimulus"
import JustValidate from "just-validate"

export default class extends Controller {
    static targets = ["form"]

    connect() {
        this.form = this.hasFormTarget ? this.formTarget : this.element

        // ---- JustValidate rules (last_name & phone optional) ----
        this.validator = new JustValidate(this.form, { validateBeforeSubmitting: true })
        this.validator
            .addField("#first_name", [{ rule: "required", errorMessage: "First name is required" }])
            .addField("#email", [
                { rule: "required", errorMessage: "Email is required" },
                { rule: "email", errorMessage: "Enter a valid email address" }
            ])
            .addField("#comments", [{ rule: "required", errorMessage: "Message cannot be empty" }])

        // Optional: light phone format guard without making it required
        const phone = this.form.querySelector("#phone_number")
        if (phone) {
            this.validator.addField("#phone_number", [
                {
                    validator: value => !value || /^[0-9+\s().-]{7,20}$/.test(value),
                    errorMessage: "Enter a valid phone number"
                }
            ])
        }

        // ---- reCAPTCHA explicit render (Turbo-safe) ----
        this.widgetEl = document.getElementById("contact-recaptcha")
        this.widgetId = null

        const tryRender = () => {
            if (window.grecaptcha?.render && this.widgetEl && this.widgetId == null) {
                this.widgetId = grecaptcha.render(this.widgetEl, {
                    sitekey: this.widgetEl.dataset.sitekey,
                    size: "invisible",
                    badge: "bottomright", // change to 'bottomleft' or 'inline' if you prefer
                    callback: (token) => this.onCaptchaSuccess(token)
                })
            } else if (this.widgetId == null) {
                setTimeout(tryRender, 50)
            }
        }
        tryRender()

        // Reset before Turbo snapshots to avoid stale iframe
        this.beforeCache = () => {
            if (this.widgetId != null && window.grecaptcha?.reset) {
                try { grecaptcha.reset(this.widgetId) } catch (_) {}
            }
        }
        document.addEventListener("turbo:before-cache", this.beforeCache)
    }

    disconnect() {
        document.removeEventListener("turbo:before-cache", this.beforeCache)
        this.validator?.destroy()
    }

    async submit(event) {
        event.preventDefault()

        const valid = await this.validator.revalidate().catch(() => false)
        if (!valid || !this.form.checkValidity()) {
            this.form.classList.add("was-validated")
            return
        }

        // Execute this form's widget instance
        if (this.widgetId != null && window.grecaptcha?.execute) {
            try { grecaptcha.reset(this.widgetId) } catch (_) {}
            grecaptcha.execute(this.widgetId)
        } else {
            // Fallback (shouldn’t happen if script loaded)
            this.showLoader()
            this.form.submit()
        }
    }

    onCaptchaSuccess(_token) {
        this.showLoader()
        this.form.submit()
        // Prepare for subsequent submits without reload
        if (this.widgetId != null && window.grecaptcha?.reset) {
            try { grecaptcha.reset(this.widgetId) } catch (_) {}
        }
    }

    showLoader() {
        this.application.getControllerForElementAndIdentifier(this.form, "form-loader")?.showLoader()
    }
}